import * as z from "zod";
import { GooglePlace } from "./google";
import { OfferingCategory } from "./offeringCategory";
import { TenantSchema } from "./openapi/lmt";
import {
    FirebaseId,
    GeoPoint,
    ISODateTime as IsoDateTimeString,
    LongString,
    ShortString,
} from "./primitives";
import { ValidateTag } from "./tags";

export const AddressComponent = z.object({
    longName: z.string(),
    shortName: z.string(),
    types: z.array(z.string()),
});

export type AddressComponent = z.infer<typeof AddressComponent>;

export const AddressComponents = z.array(AddressComponent).optional().nullable();

export type AddressComponents = z.infer<typeof AddressComponents>;

export const CatalogImage = z.object({
    id: z.string(),
    url: z.string(),
});
export type CatalogImage = z.infer<typeof CatalogImage>;

export const CityGuideLocation = z.object({
    city: z.string(),
    country: z.string(),
    key: z.string(),
});
export type CityGuideLocation = z.infer<typeof CityGuideLocation>;

export const CatalogItemTranslationDe = z.object({
    addressDe: LongString.optional().nullable(),
    benefitsDe: z.string().optional().nullable(),
    bookingPolicyDe: z.string().optional().nullable(),
    descriptionDe: z.string().optional().nullable(),
    eventDatesDe: z.string().optional().nullable(),
    headlineDe: z.string().optional().nullable(),
    localInsightsDe: z.string().optional().nullable(),
    nameDe: z.string().optional().nullable(),
    travelItineraryNotesDe: z.string().optional().nullable(),
});
export type CatalogItemTranslationDe = z.infer<typeof CatalogItemTranslationDe>;

export const CatalogItemTranslationFr = z.object({
    addressFr: LongString.optional().nullable(),
    benefitsFr: z.string().optional().nullable(),
    bookingPolicyFr: z.string().optional().nullable(),
    descriptionFr: z.string().optional().nullable(),
    eventDatesFr: z.string().optional().nullable(),
    headlineFr: z.string().optional().nullable(),
    localInsightsFr: z.string().optional().nullable(),
    nameFr: z.string().optional().nullable(),
    travelItineraryNotesFr: z.string().optional().nullable(),
});
export type CatalogItemTranslationFr = z.infer<typeof CatalogItemTranslationFr>;

const CatalogItemLanguages = CatalogItemTranslationDe.extend(
    CatalogItemTranslationFr.shape,
);

export const CatalogItemEditables = z
    .object({
        name: z.string().optional(),
        description: z.string().optional().nullable(),
        notes: z.string().optional().nullable(),
        source: z.string().optional().nullable(), // TODO: create zod enum out of CatalogSources
        googlePlaceId: z.string().optional().nullable(),
        address: LongString.optional().nullable(),
        addressComponents: AddressComponents.optional().nullable(),
        website: z.url().optional().or(z.literal("")).or(z.null()),
        phoneNumber: ShortString.optional().nullable(),
        googleUrl: z.url().optional().nullable(),
        geoPoint: GeoPoint.optional().nullable(),
        offeringCategories: z.array(OfferingCategory).optional().nullable(),
        tags: z.array(ValidateTag).optional().nullable(),
        supplierData: z
            .record(z.string(), z.object({ benefits: z.string() }))
            .optional()
            .nullable(),
        suppliers: z.array(z.string()).optional().nullable(),
        localInsights: z.string().optional().nullable(),
        storyblokArticleId: z.number().optional().nullable(),
        storyblokArticleUuid: z.string().optional().nullable(),
        storyblokArticleSlug: z.string().optional().nullable(),
        distanceToQuery: z.number().optional().nullable(),
        published: z.boolean().optional().nullable(), // TODO: Should this be required?
        images: z.array(CatalogImage).optional().nullable(), // TODO: Should this be required?
        headline: z.string().optional().nullable(),
        benefits: z.string().optional().nullable(),
        bookingPolicy: z.string().optional().nullable(),
        albertineVerified: IsoDateTimeString.optional().nullable(), // displayed as boolean if value is set
        recentlyOpened: IsoDateTimeString.optional().nullable(), // displayed as boolean if value is set
        eventDates: z.string().optional().nullable(),
        travelItineraryNotes: z.string().optional().nullable(),
        googlePlaceData: GooglePlace.optional().nullable(),
        cityGuideLocation: CityGuideLocation.optional().nullable(),
        keyDetail: z.string().optional().nullable(),
        discoverableForTenant: TenantSchema.optional().nullable(),
    })
    .extend(CatalogItemLanguages.shape);
export type CatalogItemEditables = z.infer<typeof CatalogItemEditables>;

export const CatalogItemId = FirebaseId.or(z.uuid());
export type CatalogItemId = z.infer<typeof CatalogItemId>;

export const CatalogItemPreview = CatalogItemEditables.pick({
    name: true,
    keyDetail: true,
}).extend({
    id: CatalogItemId,
    image: z.url().optional(),
});

export type CatalogItemPreview = z.infer<typeof CatalogItemPreview>;
